import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Flame } from "lucide-react";
import { Chapter, SectionTitle, Reveal } from "./Reveal";
import { MENU, MENU_PDF_URL, gaEvent } from "@/data/content";

const Menu = () => {
  const [active, setActive] = useState(MENU[0].id);
  const category = MENU.find((c) => c.id === active);

  return (
    <section id="menu" data-testid="menu-section" className="relative py-24 sm:py-36 bg-[#141210]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Chapter number="02" title="Il Menu" />
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionTitle>
            La carta, taglio per taglio.
          </SectionTitle>
          <Reveal delay={0.15}>
            <a
              data-testid="download-pdf-button"
              href={MENU_PDF_URL}
              download="Menu-John-Tartarino.pdf"
              onClick={() => gaEvent("apri_menu_pdf", { lingua: "it" })}
              className="inline-flex items-center gap-3 px-7 py-3.5 border border-[#C5A059]/60 text-[#C5A059] text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-[#0D0C0B] transition-colors duration-300"
            >
              <Download size={16} />
              Scarica il menu completo in PDF
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="mt-14">
          <div
            data-testid="menu-category-tabs"
            className="flex gap-2 overflow-x-auto pb-3 -mx-5 px-5 sm:mx-0 sm:px-0 sm:flex-wrap scrollbar-none"
          >
            {MENU.map((c) => (
              <button
                key={c.id}
                data-testid={`menu-tab-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`shrink-0 px-5 py-2.5 text-[13px] uppercase tracking-[0.15em] border transition-colors duration-300 ${
                  active === c.id
                    ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10"
                    : "border-[#F7F4EE]/10 text-[#C5BCAD] hover:border-[#C5A059]/40 hover:text-[#F7F4EE]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`menu-panel-${active}`}
              >
                <p className="font-display italic text-lg text-[#C5A059] mb-8">{category.note}</p>
                <ul className="divide-y divide-[#F7F4EE]/8">
                  {category.items.map((item) => (
                    <li
                      key={item.name}
                      data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="group py-5 flex items-baseline gap-4"
                    >
                      <div className="min-w-0">
                        <span className="font-display text-xl sm:text-2xl text-[#F7F4EE] group-hover:text-[#C5A059] transition-colors duration-300">
                          {item.name}
                        </span>
                        {item.desc && (
                          <p className="mt-1 text-sm text-[#C5BCAD]/80">{item.desc}</p>
                        )}
                      </div>
                      <span className="flex-1 border-b border-dotted border-[#C5A059]/25 translate-y-[-6px]" />
                      {item.price && (
                        <span className="font-cinzel text-base text-[#C5A059] translate-y-[-2px]">
                          {item.price}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="lg:col-span-4">
            <Reveal delay={0.1}>
              <div
                data-testid="fiorentina-highlight-card"
                className="relative border border-[#C5A059]/40 bg-[#1C1917] p-8 overflow-hidden"
              >
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#7A1C2E]/30 blur-3xl" />
                <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">
                  <Flame size={14} />
                  Il piatto guida
                </span>
                <h3 className="mt-4 font-display text-3xl text-[#F7F4EE]">La Fiorentina</h3>
                <p className="mt-4 text-sm text-[#C5BCAD] leading-relaxed">
                  Circa 1,2 kg di pura Toscana: lombata con osso e filetto,
                  sezionata e servita su piatto di ghisa caldo, con olio EVO,
                  sale e pepe.
                </p>
                <p className="mt-6 font-cinzel text-sm tracking-[0.2em] text-[#C5A059]">
                  7,00 ALL'ETTO
                </p>
                <p className="mt-1 text-xs text-[#C5BCAD]/70">
                  Chiedi in sala l'abbinamento consigliato dalla carta vicini di vigna.
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1} className="mt-12">
          <p className="text-xs text-[#C5BCAD]/60 tracking-wide">
            Pane e coperto 3,00 · Acqua microfiltrata della casa 2,00 · Vino della casa al calice 6,00 ·
            Menu bimbi disponibile · Segnalateci allergie e intolleranze
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Menu;
